import { Component, ReactNode, useCallback, useEffect, useState } from 'react';
import { ThemeProvider, Theme, StyledEngineProvider } from '@mui/material/styles';
import queryString, { ParsedQuery } from 'query-string';
import { useLocation } from 'react-router-dom';

// Files in the same folder
import { LanguageContext, TenantContext } from './contexts';
import { getTenantConfig, getTranslations } from './tenants';
import { getConfigProperty } from '../EnvironmentConfig/EnvironmentConfig';
// Types
import { LanguageType } from '../types/GeneralTypes';
import { CurrenciesObject } from '../types/Currencies';
import { SectionWiseFlagObject } from '../types/FlagsTypes';
// Utils
import { GetBuySaleCurrencyOptionList } from '../utils/GetCurrenciesData/GetCurrenciesData';
import { CheckUSAOrCanadaAffiliate } from '../utils/CheckUSAOrCanadaAffiliate/CheckUSAOrCanadaAffiliate';
// Constants
import {
  CD_GB_LEGAL_ENTITY,
  CD_USA_LEGAL_ENTITY,
  DEFAULT_LANGUAGE
} from '../utils/Constants/Constants';
import usePreviousValue from '../utils/Hooks';


declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme { }
}

export type TranslationsObject = { [key: string]: string };

type TenantProviderState = {
  language: string | null,
  settings: object,
  tenant: string | null,
  theme: object | null,
  legalEntity: string | null
  translations: TranslationsObject | null,
  currencies: CurrenciesObject | null
};

interface TenantProviderProps {
  children: ReactNode;
}

const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<string | null>(null);
  const [settings, setSettings] = useState<object>({});
  const [tenant, setTenant] = useState<string | null>(null);
  const [theme, setTheme] = useState<object | null>(null);
  const [translations, setTranslations] = useState<TranslationsObject | null>(null);
  const [legalEntity, setLegalEntity] = useState<string | null>(null);
  const [currencies, setCurrencies] = useState<CurrenciesObject>({
    sellingCurrency: [],
    buyingCurrency: [],
  });

  const prevLanguage = usePreviousValue(language);
  const prevLegalEntity = usePreviousValue(legalEntity);

  const location = useLocation();

  // Fetch Tenant Config on Mount
  useEffect(() => {
    getTenantConfig().then((config: any) => {
      const { settings, tenant, theme } = config;

      setSettings(settings);
      setTenant(tenant);
      setTheme(theme);

      const { LANGUAGE_DROPDOWN }: SectionWiseFlagObject = getConfigProperty("featureFlags");
      const preferredLanguage: LanguageType =
        (localStorage.getItem("preferredLanguage") as LanguageType) ?? DEFAULT_LANGUAGE;
      const userSelectedLanguage: LanguageType | null = LANGUAGE_DROPDOWN
        ? preferredLanguage
        : DEFAULT_LANGUAGE;

      console.log("userSelectedLanguage", userSelectedLanguage);
      updateTranslations(userSelectedLanguage);
      updateCurrencies(tenant);
    });
  }, []);

  useEffect(() => {
    if (prevLanguage !== language || prevLegalEntity !== legalEntity) {
      getUpdatedTranslations();
    }
  }, [language, legalEntity]);

  // Update Currencies
  const updateCurrencies = useCallback((tenant: string | null) => {
    const response = GetBuySaleCurrencyOptionList(tenant);
    if (response) {
      const { buyingCurrencyOptionList, sellingCurrencyOptionList } = response;
      setCurrencies({
        sellingCurrency: sellingCurrencyOptionList,
        buyingCurrency: buyingCurrencyOptionList,
      });
    }
  }, []);

  // Fetch and Update Translations
  const getUpdatedTranslations = useCallback(() => {
    let currentLanguage = language;
    let currentLegalEntity = legalEntity;

    const { ctry, le } = queryString.parse(location.search);

    if (
      (currentLegalEntity !== null && currentLegalEntity === CD_GB_LEGAL_ENTITY) ||
      (currentLegalEntity === null && ctry && le)
    ) {
      const isUSAOrCanadaAffiliate = CheckUSAOrCanadaAffiliate();
      currentLegalEntity = isUSAOrCanadaAffiliate
        ? CD_USA_LEGAL_ENTITY[currentLegalEntity || (le as string)]
        : currentLegalEntity;
    }

    getTranslations(currentLanguage, currentLegalEntity).then((translations: any) => {
      setTranslations(translations);
    });
  }, [language, legalEntity]);

  // Update Translations and Legal Entity
  const updateTranslations = useCallback(
    (language: LanguageType | null = DEFAULT_LANGUAGE, legalEntity: string | null = null) => {
      setLanguage(language);
      setLegalEntity(legalEntity);
    },
    [getUpdatedTranslations]
  );

  // Update Tenant
  const updateTenant = useCallback(
    (tenantValue: string) => {
      if (tenantValue === tenant) {
        return;
      }

      getTenantConfig().then((config: any) => {
        const { settings, tenant, theme } = config;

        setSettings(settings);
        setTenant(tenant);
        setTheme(theme);
        getUpdatedTranslations();
      });
    },
    [tenant, getUpdatedTranslations]
  );

  // Prevent rendering before theme and translations are available
  if (theme === null || translations === null) {
    return null;
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <TenantContext.Provider value={{ settings, tenant, updateTenant, currencies }}>
          <LanguageContext.Provider value={{ language, translations, updateTranslations }}>
            {children}
          </LanguageContext.Provider>
        </TenantContext.Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

// class TenantProvider extends Component<{}, TenantProviderState> {
//   constructor(props) {
//     super(props);

//     this.state = {
//       language: null,
//       settings: {},
//       tenant: null,
//       theme: null,
//       translations: null,
//       legalEntity: null,
//       currencies: {
//         sellingCurrency: [],
//         buyingCurrency: []
//       }
//     };

//     this.updateTenant = this.updateTenant.bind(this);
//     this.getUpdatedTranslations = this.getUpdatedTranslations.bind(this);
//     this.updateTranslations = this.updateTranslations.bind(this);
//     this.setCurrencies = this.setCurrencies.bind(this);
//   }

//   componentDidMount() {
//     getTenantConfig().then((config: any) => {
//       const {
//         settings,
//         tenant,
//         theme
//       }: { settings: any, tenant: string, theme: any } = config;

//       this.setState({
//         settings,
//         tenant,
//         theme
//       }, () => {
//         const { LANGUAGE_DROPDOWN }: SectionWiseFlagObject = getConfigProperty('featureFlags');
//         const preferredLanguage: LanguageType = localStorage.getItem('preferredLanguage') as LanguageType ?? DEFAULT_LANGUAGE;
//         const userSelectedLanguage: LanguageType | null = LANGUAGE_DROPDOWN ? preferredLanguage : DEFAULT_LANGUAGE;

//         console.log("userSelectedLanguage", userSelectedLanguage)
//         this.updateTranslations(userSelectedLanguage);
//         this.setCurrencies(tenant);
//       });
//     });
//   }

//   setCurrencies(tenant: string | null) {
//     const response = GetBuySaleCurrencyOptionList(tenant);
//     if (response) {
//       const { buyingCurrencyOptionList, sellingCurrencyOptionList } = response;
//       this.setState({
//         currencies: {
//           sellingCurrency: sellingCurrencyOptionList,
//           buyingCurrency: buyingCurrencyOptionList
//         }
//       });
//     }
//   }

//   getUpdatedTranslations() {
//     let { language, legalEntity } = this.state;
//     const { ctry, le }: ParsedQuery<string> = queryString.parse(history.location.search);

//     if ((legalEntity !== null && legalEntity === CD_GB_LEGAL_ENTITY) || (legalEntity === null && ctry && le)) {
//       const isUSAOrCanadaAffiliate: boolean = CheckUSAOrCanadaAffiliate();
//       legalEntity = isUSAOrCanadaAffiliate ? CD_USA_LEGAL_ENTITY[legalEntity || le as string] : legalEntity;
//     }

//     getTranslations(language, legalEntity).then((translations: any) => {
//       this.setState({
//         translations
//       });
//     });
//   }

//   updateTranslations(language: LanguageType | null = DEFAULT_LANGUAGE, legalEntity: string | null = null) {

//     this.setState({
//       language,
//       legalEntity
//     }, () => {
//       this.getUpdatedTranslations();
//     });
//   }

//   updateTenant(tenantValue: string) {
//     if (tenantValue === this.state.tenant) {
//       return;
//     }

//     getTenantConfig().then((config: any) => {
//       const {
//         settings,
//         tenant,
//         theme
//       }: { settings: any, tenant: string, theme: any } = config;

//       this.setState({
//         settings,
//         tenant,
//         theme
//       }, () => {
//         this.getUpdatedTranslations();
//       });
//     });
//   }

//   render() {
//     const {
//       language,
//       settings,
//       tenant,
//       theme,
//       translations,
//       currencies
//     } = this.state;

//     if (theme === null || translations === null) {
//       return null;
//     }

//     const {
//       children
//     } = this.props;

//     return (
//       <StyledEngineProvider injectFirst>
//         <ThemeProvider theme={theme}>
//           <TenantContext.Provider value={{ settings, tenant, updateTenant: this.updateTenant, currencies }}>
//             <LanguageContext.Provider
//               value={{
//                 language,
//                 translations,
//                 updateTranslations: this.updateTranslations
//               }}>
//               {children}
//             </LanguageContext.Provider>
//           </TenantContext.Provider>
//         </ThemeProvider>
//       </StyledEngineProvider>
//     );
//   }
// }

export default TenantProvider;
